<template>
  <header class="header--global" :class="{ fixed: scrolling }">
    <div class="section__container">
      <div class="header__content">
        <router-link to="/" class="logo-link" aria-label="DIGU home">
          <img class="header__logo" :src="logo" alt="DIGU" width="137" height="40" />
        </router-link>
        <security-desktop-menu @menu-state-change="isMenuOpen = $event" />
        <security-mobile-menu />
      </div>
    </div>
  </header>
  <!-- Content blur overlay -->
  <div v-if="isMenuOpen" class="content-blur-overlay"></div>
</template>

<script setup>
// Security-mode copy of partials/GlobalHeader.vue: same markup, same CSS, same sticky
// behaviour. It swaps in the security menus. The legacy header is untouched.
// Its container is 4rem wider than the page's .section__container, so on every page (new
// and legacy) the content starts 2rem inside the logo.
import '@/assets/css/security-theme.css';
import { ref, onMounted, onUnmounted } from 'vue';
import SecurityDesktopMenu from './SecurityDesktopMenu.vue';
import SecurityMobileMenu from './SecurityMobileMenu.vue';
import logo from '@/assets/img/Digu_Logo_Primary.webp';
import { useLegacyPageTitle } from '@/composables/security/useLegacyPageTitle';

useLegacyPageTitle();

// Marks the document as the security site, so security-theme.css can adjust legacy pages in
// security mode only (see "Legacy pages in security mode" there). Legacy mode never sets it.
document.documentElement.classList.add('sec-site');
onUnmounted(() => document.documentElement.classList.remove('sec-site'));

const scrolling = ref(false);
const isMenuOpen = ref(false);

const floatHeader = () => {
  scrolling.value = window.scrollY !== 0;
};

onMounted(() => document.addEventListener('scroll', floatHeader, { passive: true }));
onUnmounted(() => document.removeEventListener('scroll', floatHeader));
</script>

<style scoped>
.header--global {
  padding-block: 1.9rem;
  background-color: var(--sec-ground);
  transition: all 100ms ease-in-out;
  position: relative;
  z-index: 1001;
}

.section__container {
  /* Page content is 85vw, max 1280px (App.vue); the logo sits 2rem outside it. */
  width: min(calc(85vw + 4rem), calc(100vw - 2rem));
  max-width: calc(1280px + 4rem);
  margin: 0 auto;
  padding: 0;
}

.header__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.logo-link {
  flex-shrink: 0;
}

.fixed {
  padding-inline: 1rem !important;
  z-index: 1001;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  box-shadow: 0px 8px 16px var(--sec-header-shadow);
}

.content-blur-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--sec-blur-overlay);
  backdrop-filter: blur(4px);
  z-index: 1000;
  pointer-events: none;
}

.header__logo {
  height: 2.25rem;
  /* width/height attributes give the intrinsic ratio (3425x1000) for layout; CSS sets the size. */
  width: auto;
  transition: height 50ms ease-in-out;
}

@media only screen and (min-width: 1280px) {
  .header__logo {
    height: 2rem;
  }

  .fixed .header__logo {
    height: 2rem;
  }
}
</style>
